/**
 * Navegação → atividade: uma "página vista" por troca de rota, e as buscas.
 *
 * Mora em `app/` porque conhece as rotas e a navegação (`layout/nav`); a fila
 * e o envio são do `core/tracking`.
 */
import { track } from '@/core/tracking';
import { navEntryFor } from './layout/nav';

/** Rotas fora do menu, com o nome que o backoffice mostra. */
const PAGES: [RegExp, string][] = [
  [/^\/assistente\/?$/, 'Centuria'],
  [/^\/cursos\/favoritos\/?$/, 'Aulas favoritas'],
  [/^\/cursos\/historico\/?$/, 'Histórico de aulas'],
  [/^\/cursos\/novo\/?$/, 'Novo curso'],
  [/^\/cursos\/[^/]+\/assistir\/?$/, 'Aula'],
  [/^\/cursos\/[^/]+\/?$/, 'Curso'],
  [/^\/eventos\/[^/]+\/?$/, 'Evento'],
  [/^\/login\/?$/, 'Entrar'],
];

/** Nome legível da página — o que estiver aberto (curso, evento) vai no próprio evento de visualização. */
export function pageLabel(pathname: string): string | undefined {
  return PAGES.find(([rx]) => rx.test(pathname))?.[1] ?? navEntryFor(pathname)?.label;
}

/** O termo de busca da URL (`?busca=`), aparado. Só o tamanho dele sai daqui. */
export function searchTermOf(search: unknown): string {
  const value = (search as { busca?: unknown } | undefined)?.busca;
  return typeof value === 'string' ? value.trim() : '';
}

/** Busca: conta quando a pessoa para de digitar. */
export const SEARCH_SETTLE_MS = 1_500;
const MIN_SEARCH_CHARS = 2;

interface Location {
  pathname: string;
  search: unknown;
}

interface NavigationEvent {
  fromLocation?: Location;
  toLocation: Location;
  pathChanged: boolean;
}

/** Liga a navegação do router à atividade. Devolve o desligamento. */
export function trackNavigation(router: {
  subscribe: (event: 'onResolved', fn: (e: NavigationEvent) => void) => () => void;
}): () => void {
  let pendingSearch: number | undefined;
  const unsubscribe = router.subscribe('onResolved', ({ fromLocation, toLocation, pathChanged }) => {
    const { pathname } = toLocation;
    // Revalidar a rota (login, sessão renovada) não é uma visita nova.
    if (pathChanged || !fromLocation) {
      track({ action: 'page.viewed', path: pathname, targetLabel: pageLabel(pathname) });
    }
    const term = searchTermOf(toLocation.search);
    if (term === searchTermOf(fromLocation?.search)) return;
    window.clearTimeout(pendingSearch);
    if (term.length < MIN_SEARCH_CHARS) return;
    pendingSearch = window.setTimeout(
      () => track({ action: 'search.performed', path: pathname, metadata: { chars: term.length } }),
      SEARCH_SETTLE_MS,
    );
  });
  return () => {
    window.clearTimeout(pendingSearch);
    unsubscribe();
  };
}
